/**
 * SessionExpiredHandler - Global handler for 401 Unauthorized responses
 *
 * Listens for 'auth:session-expired' events dispatched by apiFetch on 401 responses.
 * Signs the user out and shows a toast message to prevent broken half-logged-in states.
 *
 * Must be mounted inside both AuthProvider (for signOut) and ToastProvider (for toast).
 */

import { useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from './ui/Toast';

export function SessionExpiredHandler() {
  const { signOut } = useAuth();
  const { toast } = useToast();

  // Debounce ref to ignore rapid-fire 401s (e.g., parallel requests all failing)
  const lastHandledRef = useRef<number>(0);
  const DEBOUNCE_MS = 2000;

  useEffect(() => {
    const handleSessionExpired = async () => {
      const now = Date.now();

      // Ignore if we handled a session expiry within the debounce window
      if (now - lastHandledRef.current < DEBOUNCE_MS) {
        return;
      }

      lastHandledRef.current = now;

      // Sign out first, then show toast
      try {
        await signOut();
      } catch (err) {
        console.error('[SessionExpiredHandler] Error signing out:', err);
      }

      toast.error('Session expired. Please sign in again.');
    };

    window.addEventListener('auth:session-expired', handleSessionExpired);

    return () => {
      window.removeEventListener('auth:session-expired', handleSessionExpired);
    };
  }, [signOut, toast]);

  // Render nothing - this is a side-effect-only component
  return null;
}
